import { createContext } from "react";

import { GetLimitsResponse } from "src/gen/schema/configd/api";
import { GetUserContextsResponse } from "src/gen/schema/fred/fapi";
import { UserRole } from "src/gen/schema/models/auth";
import { UserSessionResponse } from "src/gen/schema/models/user";
import { unreachable } from "src/utils/switch";

import { logger } from "./globals";

/**
 * All the params and callbacks
 * needed to manage login/logout
 * sessions
 */
interface LoginSchema {
  session?: UserSessionResponse | undefined;
  isReadOnly: boolean;
  isAdmin: boolean;
  configLimits?: GetLimitsResponse;
  logout: (e?: React.MouseEvent<unknown>) => void;
  changePassword?: (cg: () => void) => void;
  getUserSession: () => Promise<boolean>;
  userContexts?: GetUserContextsResponse | undefined;
  setUserContexts?: (resp: GetUserContextsResponse) => void;
  switchTenant: (id?: string) => void;
}

export const isAdminUser = (role: UserRole): boolean => {
  switch (role) {
    case UserRole.ADMIN:
      return true;
    case UserRole.READ_WRITE:
    case UserRole.READ_ONLY:
    case UserRole.USER_ROLE_UNSPECIFIED:
    case UserRole.USER_ROLE_NONE:
    case UserRole.UNRECOGNIZED:
      return false;
    default:
      unreachable(role);
      return false;
  }
};

export const isReadOnlyUser = (role: UserRole): boolean => {
  switch (role) {
    case UserRole.ADMIN:
    case UserRole.READ_WRITE:
      return false;
    case UserRole.READ_ONLY:
      return true;
    case UserRole.USER_ROLE_UNSPECIFIED:
    case UserRole.USER_ROLE_NONE:
    case UserRole.UNRECOGNIZED:
      logger.error(`Unsupported user role: ${role}`);
      // default to read-only
      return true;
    default:
      unreachable(role);
      return false;
  }
};

export const LoginContext = createContext<LoginSchema>({
  session: undefined,
  isReadOnly: true,
  isAdmin: false,
  configLimits: undefined,
  logout: () => {
    window.location.reload();
  },
  getUserSession: () => Promise.resolve(true),
  switchTenant: () => {
    window.location.reload();
  }
});
